import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Users, Building2, Landmark, HeartPulse, TrendingUp, Flag } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import Reveal from "@/components/Reveal";
import ContactForm from "@/components/ContactForm";
import NewsletterForm from "@/components/NewsletterForm";
import SEO, { orgJsonLd } from "@/components/SEO";

const DIVISIONS = [
  {
    id: 1, icon: "✦", to: "/",
    title: "Behavioral Health Consulting",
    body: "Operational, clinical, regulatory, and executive support for behavioral healthcare organizations.",
    bullets: ["Licensing and accreditation", "Clinical operations and documentation", "Executive and fractional leadership", "Quality, risk, and performance systems"],
    primary: true,
  },
  {
    id: 2, icon: "◎", to: "/hr",
    title: "Catalyst HR Consulting",
    body: "Strategic people operations for healthcare organizations and growing businesses across industries.",
    bullets: ["Fractional HR leadership", "Compliance audits and handbooks", "Employee relations & investigations", "Recruiting, culture, and retention"],
  },
  {
    id: 3, icon: "∞", to: "/continuum",
    title: "Catalyst Continuum",
    body: "Complete behavioral health program models, startup packages, curricula, and implementation support.",
    bullets: ["Detox, Residential, BHRF, PHP, IOP, OP", "Policies, forms, workflows, staffing", "Clinical curricula & training systems", "Launch and optimization packages"],
  },
  {
    id: 4, icon: "▲", to: "/academy",
    title: "Catalyst Academy",
    body: "Digital downloads, toolkits, courses, and memberships that scale our expertise to your team.",
    bullets: ["Editable templates & manuals", "Cohort and self-paced courses", "Partner Portal membership", "Enterprise licenses"],
  },
];

const AUDIENCES = [
  { icon: <Sparkles size={18} />, label: "Startups", body: "From concept and licensure to first admission." },
  { icon: <Building2 size={18} />, label: "Existing Providers", body: "Optimize quality, census, and operations." },
  { icon: <TrendingUp size={18} />, label: "Multi-Site Organizations", body: "Standardize and scale across locations." },
  { icon: <HeartPulse size={18} />, label: "Hospitals", body: "Integrate behavioral health across the system." },
  { icon: <Users size={18} />, label: "Nonprofits", body: "Sustainable operations for mission-driven work." },
  { icon: <Landmark size={18} />, label: "Private Equity", body: "Diligence, integration, and value creation." },
  { icon: <Flag size={18} />, label: "Tribal Organizations", body: "Culturally-grounded program development." },
];

const SOLUTIONS = [
  { n: "01", label: "Accreditation", body: "Joint Commission, CARF, COA readiness and survey management." },
  { n: "02", label: "HR", body: "Fractional HR, compliance, investigations, and handbooks." },
  { n: "03", label: "Operations", body: "Staffing, workflow, utilization, and productivity systems." },
  { n: "04", label: "Clinical Excellence", body: "Documentation, curricula, and clinical quality." },
  { n: "05", label: "Leadership", body: "Executive coaching, fractional leadership, and academies." },
  { n: "06", label: "Compliance", body: "State licensing, corporate compliance, audit response." },
  { n: "07", label: "Technology", body: "EHR selection, dashboards, and workflow automation." },
];

export default function Home() {
  return (
    <SiteLayout branch="main">
      <SEO
        title="Behavioral Health & HR Consulting"
        description="Catalyst helps behavioral healthcare organizations build, grow, and thrive — through advisory, HR consulting, program development, and digital resources."
        path="/"
        jsonLd={orgJsonLd}
      />
      {/* HERO */}
      <section
        className="hero grain relative overflow-hidden"
        style={{
          minHeight: 720,
          display: "grid",
          gridTemplateColumns: "1.15fr .85fr",
          background: "var(--navy)",
          color: "white",
          padding: "8vw 6vw 7vw",
          gap: "7vw",
          alignItems: "center",
        }}
        data-testid="hero"
      >
        <Reveal>
          <span className="eyebrow">ONE PARTNER · EVERY STAGE OF GROWTH</span>
          <h1 className="font-display mt-5 mb-5" style={{ fontSize: "clamp(42px, 5vw, 72px)", lineHeight: 1.05 }}>
            Helping behavioral healthcare organizations{" "}
            <em className="text-[var(--gold)] not-italic">build, grow, and thrive.</em>
          </h1>
          <p className="text-neutral-300 text-[17px] leading-[1.75] max-w-[640px]">
            Catalyst combines behavioral health operations, strategic HR consulting, program development,
            and workforce solutions under one integrated platform — with the practical implementation to
            make it real.
          </p>
          <div className="flex flex-wrap gap-3 mt-8" data-testid="hero-actions">
            <Link to="/book" className="btn btn-gold" data-testid="hero-book-btn">
              Book a Consultation <ArrowRight className="ml-2" size={16} />
            </Link>
            <a href="#solutions" className="btn btn-outline" data-testid="hero-explore-btn">
              Explore Our Divisions
            </a>
          </div>
          <div className="flex flex-wrap gap-8 mt-12 pt-8 border-t border-[var(--line-dark)] text-neutral-400 text-[11px] tracking-widest uppercase">
            {[
              ["20+", "Years of Expertise"],
              ["Nationwide", "Reach & Support"],
              ["Practical", "Implementation"],
              ["Multi-Site", "Scalable Systems"],
            ].map(([n, l]) => (
              <div key={l}>
                <b className="block text-white text-base tracking-normal normal-case mb-1">{n}</b>
                <span>{l}</span>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal className="hidden lg:block">
          <div className="bg-[var(--navy-2)] border border-[var(--line-dark)] p-8 text-center relative">
            <div className="text-[10px] tracking-[0.22em] text-[var(--gold)] font-bold">
              THE CATALYST MODEL
            </div>
            <div className="orbit orbit-rings">
              <div className="orbit-center">CATALYST</div>
              <div className="orbit-item i1">BH<br />Consulting</div>
              <div className="orbit-item i2">HR<br />Consulting</div>
              <div className="orbit-item i3">Continuum<br />Programs</div>
              <div className="orbit-item i4">Academy<br />& Tools</div>
            </div>
            <p className="text-[13px] leading-relaxed text-neutral-300 mt-4">
              Advisory, implementation, digital products, training, and recurring partnerships — one team,
              one strategy.
            </p>
          </div>
        </Reveal>
      </section>

      <div className="brand-strip">
        <span>BEHAVIORAL HEALTH</span><i /><span>HUMAN RESOURCES</span><i /><span>PROGRAM DEVELOPMENT</span>
        <i /><span>LEADERSHIP</span><i /><span>COMPLIANCE</span>
      </div>

      {/* SOLUTIONS / DIVISIONS */}
      <section id="solutions" className="section bg-[var(--light)]">
        <Reveal className="max-w-3xl mx-auto text-center mb-14">
          <span className="eyebrow">OUR DIVISIONS</span>
          <h2 className="font-display mt-5 mb-5 text-[clamp(36px,4vw,56px)] leading-[1.05] text-[var(--navy)]">
            Specialized expertise. <em className="italic text-[var(--gold)]">One connected strategy.</em>
          </h2>
          <p className="text-[var(--muted)] leading-relaxed">
            Enter through the solution that fits your immediate need — then scale support as your
            organization grows.
          </p>
        </Reveal>

        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4 max-w-[1400px] mx-auto items-stretch" data-testid="divisions-grid">
          {DIVISIONS.map((d, i) => (
            <Reveal key={d.id} delay={i * 80} className="h-full">
              <article
                className={`card-editorial h-full flex flex-col ${d.primary ? "!bg-[var(--navy-2)] !text-white !border-[var(--navy-2)]" : ""}`}
                data-testid={`division-${d.id}`}
              >
                <span className={`absolute right-6 top-5 text-[11px] tracking-widest ${d.primary ? "text-neutral-400" : "text-neutral-500"}`}>0{d.id}</span>
                <div className="text-4xl text-[var(--gold)] my-6">{d.icon}</div>
                <h3 className="font-display text-[26px] leading-tight mb-3">{d.title}</h3>
                <p className={`text-sm leading-relaxed ${d.primary ? "text-neutral-300" : "text-[var(--muted)]"} mb-6`}>{d.body}</p>
                <ul className={`text-sm leading-loose list-disc pl-5 mb-6 ${d.primary ? "text-neutral-300" : "text-[var(--muted)]"}`}>
                  {d.bullets.map((b) => <li key={b}>{b}</li>)}
                </ul>
                <Link to={d.to} className="text-link mt-auto" data-testid={`division-${d.id}-link`}>
                  Explore <ArrowRight size={14} />
                </Link>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section id="who-we-serve" className="section bg-[var(--navy)] text-white">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-16 max-w-[1300px] mx-auto">
          <Reveal>
            <span className="eyebrow">WHO WE SERVE</span>
            <h2 className="font-display mt-5 text-[clamp(36px,4vw,52px)] leading-[1.05]">
              From single-site startups to national platforms.
            </h2>
            <p className="text-neutral-300 leading-relaxed mt-5 mb-8">
              We work with organizations at every stage — helping them scale operations, strengthen
              compliance, and elevate care.
            </p>
            <Link to="/book" className="text-link">Discuss your organization <ArrowRight size={14} /></Link>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-4 items-stretch auto-rows-fr">
            {AUDIENCES.map((a, i) => (
              <Reveal key={a.label} delay={i * 60} className="h-full">
                <div className="border border-[var(--line-dark)] bg-[var(--navy-2)] p-6 h-full hover:border-[var(--gold)] transition-colors">
                  <div className="text-[var(--gold)] mb-3">{a.icon}</div>
                  <div className="font-display text-xl mb-2">{a.label}</div>
                  <p className="text-neutral-400 text-sm leading-relaxed">{a.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTIONS DEEP */}
      <section id="capabilities" className="section bg-[var(--cream)]">
        <Reveal className="max-w-3xl mx-auto text-center mb-14">
          <span className="eyebrow-dark">CAPABILITIES</span>
          <h2 className="font-display mt-5 text-[clamp(36px,4vw,52px)] leading-[1.05] text-[var(--navy)]">
            Where we drive measurable change.
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-7 gap-0 max-w-[1350px] mx-auto border-t border-l border-[var(--line)] items-stretch auto-rows-fr">
          {SOLUTIONS.map((s) => (
            <Link
              key={s.n}
              to="/solutions"
              className="border-r border-b border-[var(--line)] bg-white/60 p-6 hover:bg-white transition-colors flex flex-col h-full group"
              data-testid={`solution-${s.n}`}
              aria-label={`${s.label} — read more on Solutions`}
            >
              <div className="text-[var(--gold)] text-[10px] tracking-widest font-bold mb-3">{s.n}</div>
              <div className="font-display text-xl text-[var(--navy)] mb-3 group-hover:underline decoration-[var(--gold)] decoration-2 underline-offset-4">{s.label}</div>
              <p className="text-[var(--muted)] text-xs leading-relaxed">{s.body}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* EXPLORE — SEO-focused internal navigation strip */}
      <section className="section bg-white" aria-labelledby="explore-heading">
        <div className="max-w-[1200px] mx-auto">
          <Reveal className="max-w-2xl mb-10">
            <span className="eyebrow-dark">EXPLORE THE PLATFORM</span>
            <h2 id="explore-heading" className="font-display mt-5 text-[clamp(30px,3vw,44px)] leading-[1.1] text-[var(--navy)]">
              Every service, product, and resource in one place.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="home-internal-nav">
            {[
              {
                title: "Divisions",
                items: [
                  { to: "/hr", label: "HR Consulting" },
                  { to: "/continuum", label: "Catalyst Continuum" },
                  { to: "/academy", label: "Catalyst Academy" },
                ],
              },
              {
                title: "Solutions",
                items: [
                  { to: "/solutions", label: "Full Solutions Overview" },
                  { to: "/who-we-serve", label: "Who We Serve" },
                  { to: "/about", label: "About Catalyst" },
                ],
              },
              {
                title: "Store",
                items: [
                  { to: "/academy?category=Bundles", label: "Curriculum Bundles" },
                  { to: "/academy?category=Toolkits", label: "Operational Toolkits" },
                  { to: "/academy?category=Courses", label: "Self-Paced Courses" },
                  { to: "/academy?category=Memberships", label: "Partner Portal" },
                ],
              },
              {
                title: "Insights & Contact",
                items: [
                  { to: "/blog", label: "Insights & Articles" },
                  { to: "/tools", label: "Free Calculators" },
                  { to: "/book", label: "Book a Consultation" },
                  { to: "/press", label: "Press & Media Kit" },
                  { to: "/login", label: "Member Portal" },
                ],
              },
            ].map((col) => (
              <div key={col.title}>
                <h3 className="text-[10px] tracking-[0.25em] uppercase text-[var(--gold)] font-bold mb-4">{col.title}</h3>
                <ul className="space-y-2">
                  {col.items.map((it) => (
                    <li key={it.to}>
                      <Link
                        to={it.to}
                        className="text-[var(--navy)] hover:text-[var(--gold)] text-sm inline-flex items-center gap-1.5 transition-colors"
                      >
                        <ArrowRight size={12} className="text-[var(--gold)]" /> {it.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS TEASER */}
      <FeaturedProductsTeaser />

      {/* TESTIMONIALS */}
      <section className="section bg-white">
        <div className="max-w-[1200px] mx-auto grid lg:grid-cols-3 gap-8 items-stretch">
          {[
            {
              q: "Catalyst rebuilt our documentation system and helped us pass a Joint Commission survey with zero findings. Practical, not theoretical.",
              n: "Executive Director",
              r: "Multi-state BH provider",
            },
            {
              q: "Their fractional HR model gave us senior expertise without full-time overhead. Turnover dropped 32% in six months.",
              n: "COO",
              r: "Behavioral Health Group",
            },
            {
              q: "The Continuum program pack is the reason we opened four weeks ahead of schedule. Nothing was theoretical — everything was usable.",
              n: "CEO & Founder",
              r: "Arizona IOP",
            },
          ].map((t, i) => (
            <Reveal key={i} delay={i * 80} className="h-full">
              <blockquote className="border-t-2 border-[var(--navy)] pt-6 h-full flex flex-col">
                <p className="font-display text-[22px] leading-[1.35] text-[var(--navy)] mb-6 flex-1">
                  "{t.q}"
                </p>
                <footer className="mt-auto">
                  <div className="text-[var(--navy)] font-bold text-sm">{t.n}</div>
                  <div className="text-[var(--muted)] text-xs tracking-widest uppercase mt-1">{t.r}</div>
                </footer>
              </blockquote>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA CONTACT */}
      <section id="contact" className="bg-[var(--navy)] text-white grid lg:grid-cols-[0.8fr_1.2fr] gap-16 px-[6vw] py-[100px]">
        <Reveal>
          <span className="eyebrow">START A CONVERSATION</span>
          <h2 className="font-display mt-5 text-[clamp(38px,4vw,58px)] leading-[1.05]">
            What does your organization need to build next?
          </h2>
          <p className="text-neutral-300 leading-relaxed mt-5">
            Send us a note or book a discovery call. We'll match you with the Catalyst division best
            suited to help.
          </p>
          <div className="flex flex-col gap-3 mt-8 text-neutral-300 text-sm">
            <span>☎ (623) 324-3237</span>
            <span>✉ info@catalystbhconsulting.com</span>
            <span>⌖ Phoenix, Arizona · Nationwide</span>
          </div>
          <Link to="/book" className="btn btn-gold mt-8 inline-flex" data-testid="footer-book-btn">
            Book a Consultation <ArrowRight className="ml-2" size={16} />
          </Link>
        </Reveal>
        <Reveal>
          <ContactForm source="home" />
        </Reveal>
      </section>

      <section className="section" data-testid="newsletter-section">
        <NewsletterForm variant="hero" source="homepage" />
      </section>
    </SiteLayout>
  );
}

function FeaturedProductsTeaser() {
  return (
    <section className="section" style={{ background: "#17304a", color: "white" }} data-testid="academy-teaser">
      <div className="grid lg:grid-cols-2 gap-16 max-w-[1300px] mx-auto items-center">
        <Reveal>
          <span className="eyebrow">CATALYST ACADEMY · FEATURED PRODUCTS</span>
          <h2 className="font-display mt-5 text-[clamp(36px,4vw,54px)] leading-[1.05]">
            Turn our expertise into <em className="italic text-[var(--gold)]">tools your team can use today.</em>
          </h2>
          <p className="text-neutral-300 leading-relaxed mt-5">
            Digital downloads, toolkits, courses, and memberships. Everything is built from our
            consulting engagements — practical, editable, and ready to deploy.
          </p>
          <div className="flex gap-3 mt-8">
            <Link to="/academy" className="btn btn-gold">Visit the Academy</Link>
          </div>
        </Reveal>

        <div className="flex flex-col gap-3">
          {[
            ["COURSE", "Behavioral Health Leadership Academy"],
            ["TOOLKIT", "12-Week IOP Curriculum Library"],
            ["DOWNLOAD", "Survey Readiness Quick-Check"],
            ["MEMBERSHIP", "Partner Portal — Annual"],
          ].map(([tag, name], i) => (
            <Reveal key={name} delay={i * 60}>
              <Link
                to="/academy"
                className="flex items-center justify-between px-8 py-6 bg-[#10273e] border-l-4 border-[var(--gold)] hover:bg-[#122f4a] transition-colors group"
                data-testid={`teaser-${i}`}
              >
                <div>
                  <div className="text-[var(--gold)] text-[10px] tracking-[0.2em] mb-2">{tag}</div>
                  <div className="font-display text-xl">{name}</div>
                </div>
                <ArrowRight size={18} className="text-[var(--gold)] group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
